package com.partejar.app.feature.profile.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.core.session.SessionIdentityStore
import com.partejar.app.feature.profile.data.UserProfile
import com.partejar.app.feature.profile.data.UserProfileDataSource
import com.partejar.app.feature.profile.data.UserProfessionalRegistration
import com.partejar.app.feature.profile.data.UserProfessionalRegistrationInput
import com.partejar.app.feature.workspace.data.SessionUser
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface ProfileUiState {
    data object Loading : ProfileUiState
    data class Content(val profile: UserProfile) : ProfileUiState
    data class Error(val message: String) : ProfileUiState
}

sealed interface ProfileSubmitState {
    data object Idle : ProfileSubmitState
    data object Submitting : ProfileSubmitState
    data object Success : ProfileSubmitState
    data class Error(val message: String) : ProfileSubmitState
}

sealed interface ProfessionalRegistrationsUiState {
    data object Loading : ProfessionalRegistrationsUiState
    data class Content(val registrations: List<UserProfessionalRegistration>) : ProfessionalRegistrationsUiState
    data class Error(val message: String) : ProfessionalRegistrationsUiState
}

class ProfileViewModel private constructor(
    private val dataSource: UserProfileDataSource,
    private val sessionIdentityStore: SessionIdentityStore,
    private val scopeOverride: CoroutineScope?,
    @Suppress("UNUSED_PARAMETER") constructorMarker: Unit
) : ViewModel() {
    constructor(dataSource: UserProfileDataSource, sessionIdentityStore: SessionIdentityStore) :
        this(dataSource, sessionIdentityStore, null, Unit)

    internal constructor(
        dataSource: UserProfileDataSource,
        sessionIdentityStore: SessionIdentityStore,
        scope: CoroutineScope
    ) : this(dataSource, sessionIdentityStore, scope, Unit)

    private val scope get() = scopeOverride ?: viewModelScope
    private val _uiState = MutableStateFlow<ProfileUiState>(ProfileUiState.Loading)
    val uiState: StateFlow<ProfileUiState> = _uiState.asStateFlow()
    private val _submitState = MutableStateFlow<ProfileSubmitState>(ProfileSubmitState.Idle)
    val submitState: StateFlow<ProfileSubmitState> = _submitState.asStateFlow()
    private val _registrationsState = MutableStateFlow<ProfessionalRegistrationsUiState>(ProfessionalRegistrationsUiState.Loading)
    val registrationsState: StateFlow<ProfessionalRegistrationsUiState> = _registrationsState.asStateFlow()

    init {
        load()
    }

    fun load() {
        _uiState.value = ProfileUiState.Loading
        scope.launch {
            _uiState.value = try {
                ProfileUiState.Content(dataSource.get()).also { loadRegistrations() }
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                ProfileUiState.Error("Não foi possível carregar o perfil.")
            }
        }
    }

    fun loadRegistrations() {
        _registrationsState.value = ProfessionalRegistrationsUiState.Loading
        scope.launch {
            _registrationsState.value = try {
                ProfessionalRegistrationsUiState.Content(dataSource.registrations())
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                ProfessionalRegistrationsUiState.Error("Não foi possível carregar os registros profissionais.")
            }
        }
    }

    fun saveName(name: String, cpf: String? = null) {
        val normalizedName = name.trim().replace(Regex("\\s+"), " ")
        if (normalizedName.isBlank()) {
            _submitState.value = ProfileSubmitState.Error("Informe seu nome.")
            return
        }
        _submitState.value = ProfileSubmitState.Submitting
        scope.launch {
            _submitState.value = try {
                val profile = dataSource.update(normalizedName, cpf)
                _uiState.value = ProfileUiState.Content(profile)
                sessionIdentityStore.updateIdentity(SessionUser(profile.id, profile.name, profile.email))
                ProfileSubmitState.Success
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                ProfileSubmitState.Error("Não foi possível salvar suas alterações.")
            }
        }
    }

    fun clearSubmitState() {
        _submitState.value = ProfileSubmitState.Idle
    }

    fun saveRegistration(id: Long?, input: UserProfessionalRegistrationInput) {
        _submitState.value = ProfileSubmitState.Submitting
        scope.launch {
            _submitState.value = try {
                val saved = if (id == null) dataSource.createRegistration(input) else dataSource.updateRegistration(id, input)
                val current = (_registrationsState.value as? ProfessionalRegistrationsUiState.Content)?.registrations.orEmpty()
                _registrationsState.value = ProfessionalRegistrationsUiState.Content(
                    (current.filterNot { it.id == saved.id } + saved).sortedWith(compareByDescending<UserProfessionalRegistration> { it.active }.thenBy { it.council }.thenBy { it.number })
                )
                ProfileSubmitState.Success
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                ProfileSubmitState.Error("Não foi possível salvar o registro profissional.")
            }
        }
    }

    fun deactivateRegistration(id: Long) {
        _submitState.value = ProfileSubmitState.Submitting
        scope.launch {
            _submitState.value = try {
                dataSource.deactivateRegistration(id)
                val current = (_registrationsState.value as? ProfessionalRegistrationsUiState.Content)?.registrations.orEmpty()
                _registrationsState.value = ProfessionalRegistrationsUiState.Content(current.map { if (it.id == id) it.copy(active = false) else it })
                ProfileSubmitState.Success
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                ProfileSubmitState.Error("Não foi possível inativar o registro profissional.")
            }
        }
    }
}
